import dayjs from 'dayjs';
import { takeLatest, call, put, select } from 'redux-saga/effects';
import axios from '../axiosConfig';
import {
  getInsuranceQuotesRequest,
  getInsuranceQuotesSuccess,
  getInsuranceQuotesFailure,
  clearInsuranceSelection,
  selectInsuranceTripInfo,
  selectInsuranceHasNoArchRoamAccount,
} from '../../slices/insurance';
import { getInsuranceTripInfo } from '../../../helpers/InsuranceHelper';
import { InsuranceMode } from '../../../constants';

function* getInsuranceQuotesWorker({ payload: { tripId, agentId } }) {
  try {
    yield put(clearInsuranceSelection({ tripId }));

    const trip = yield select((state) => state.trip.selectedTrips[tripId]);
    const passengers = yield select((state) => state.trip.passengers);

    if (!trip) {
      throw new Error('Trip not found');
    }

    if (!passengers || Object.keys(passengers).length === 0) {
      throw new Error('Passenger information is required for insurance quotes');
    }

    const insuranceTripInfo =
      trip.insuranceItinerary?.insuranceMode === InsuranceMode.Standalone
        ? yield select(selectInsuranceTripInfo({ tripId }))
        : getInsuranceTripInfo(trip);

    const passengersRequest = Object.values(passengers).map((item) => {
      const passenger = {
        firstName: item.firstName,
        lastName: item.lastName,
        birthDate: item.birthDate ? dayjs(item.birthDate).format('YYYY-MM-DD') : undefined,
        contactEmail: item.contactEmail,
        phone: item.phone,
        address: item.address,
        countryCode: item.countryCode,
        stateProvCode: item.stateProvCode,
        gender: item.gender || '',
      };
      return passenger;
    });

    const requestData = {
      tripInfo: {
        startDate: dayjs(insuranceTripInfo.startDate).format('YYYY-MM-DD'),
        endDate: dayjs(insuranceTripInfo.endDate).format('YYYY-MM-DD'),
        depositDate: dayjs(insuranceTripInfo.depositDate).format('YYYY-MM-DD'),
        tripCost: insuranceTripInfo.tripCost,
        destinations: insuranceTripInfo.destinations,
      },
      passengers: passengersRequest,
      agentId: agentId || null,
    };

    const config = {
      method: 'POST',
      url: '/api/insurance/getquotes',
      data: requestData,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000,
    };

    const { data } = yield call(axios, config);

    if (!data?.success) {
      yield put(
        getInsuranceQuotesFailure({
          tripId,
          errors: data?.errorMessages,
          ineligibilityMessages: data?.ineligibilityMessages,
          invalidAgentId: data?.invalidAgentId ?? false,
        }),
      );
      return;
    }

    yield put(
      getInsuranceQuotesSuccess({
        tripId,
        quotes: data.quotes,
        retrievedAt: data.retrievedAt || new Date().toISOString(),
        ineligibilityMessages: data.ineligibilityMessages,
      }),
    );
  } catch (error) {
    let errorMessage = 'Unable to retrieve insurance quotes at this time';

    if (error.response) {
      // API error response
      const { status } = error.response;
      if (status >= 500) {
        errorMessage = 'Insurance service is temporarily unavailable. Please try again later.';
      } else if (status === 404) {
        errorMessage = 'Insurance service not available for this trip.';
      } else {
        errorMessage =
          error.response.data?.errorMessage ||
          error.response.data?.message ||
          'Unable to retrieve insurance quotes at this time.';
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    yield put(
      getInsuranceQuotesFailure({
        tripId,
        errors: [errorMessage],
      }),
    );
  }
}

export default function* insuranceWatcher() {
  yield takeLatest(getInsuranceQuotesRequest.toString(), getInsuranceQuotesWorker);
}
